import type {
  BridgeMessage,
  BridgeResponse,
  BridgeInitMessage,
  PluginToHostMessageType,
  FiasTheme,
  FiasVendoredAssets,
  PluginPermission,
  EntityInvocationResult,
  SubscriptionEndedReason,
} from './types';
import { getDefaultTheme } from './theme';
import { SDK_BRIDGE_PROTOCOL_VERSION, SDK_REQUIRES_HOST_PROTOCOL_VERSION } from './protocol';

let messageCounter = 0;

function generateMessageId(): string {
  return `msg_${Date.now()}_${++messageCounter}`;
}

/**
 * Error type for every rejection the bridge produces.
 *
 * Plugins routinely `JSON.stringify` a caught error to log or display it —
 * and `JSON.stringify(new Error(...))` yields `{}` because Error's props are
 * non-enumerable. That's the Rota-incident failure shape: an entity_invoke
 * rejection reached plugin code as an opaque `{}` and the plugin crashed on
 * the missing result. `toJSON()` makes stringification meaningful, and the
 * machine-readable `code` lets plugin code branch on failure kinds
 * (`TIMEOUT`, `BRIDGE_DESTROYED`, server codes like `BRIDGE_TOKEN_EXPIRED`).
 *
 * WIRE-FORMAT NOTE: the host posts plain fields (`{ error: string,
 * errorCode?: string }`) in the response message; the SDK constructs this
 * class locally. Never rely on structured-cloning an Error across the
 * postMessage boundary.
 */
export class FiasBridgeError extends Error {
  code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'FiasBridgeError';
    this.code = code;
  }

  toJSON(): { name: string; code: string; message: string } {
    return { name: 'FiasBridgeError', code: this.code, message: this.message };
  }
}

/**
 * Merges an incoming theme with defaults to ensure all fields are present.
 * Protects plugins from crashing when the host sends an incomplete theme.
 */
function mergeThemeWithDefaults(incoming: Partial<FiasTheme>): FiasTheme {
  const defaults = getDefaultTheme(incoming.mode ?? 'dark');
  return {
    ...defaults,
    ...incoming,
    colors: { ...defaults.colors, ...(incoming.colors ?? {}) },
    spacing: { ...defaults.spacing, ...(incoming.spacing ?? {}) },
    fonts: { ...defaults.fonts, ...(incoming.fonts ?? {}) },
    components: { ...defaults.components, ...(incoming.components ?? {}) },
  };
}

type PendingRequest = {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  onToken?: (text: string) => void;
};

const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Low-level bridge client for postMessage communication with the FIAS host frame.
 * Used internally by SDK hooks. Plugin developers should use hooks instead.
 */
export class FiasBridge {
  private pendingRequests = new Map<string, PendingRequest>();
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  private initResolve: (() => void) | null = null;
  private archId: string = '';
  private permissions: PluginPermission[] = [];
  private theme: FiasTheme | null = null;
  private vendoredAssets: FiasVendoredAssets | null = null;
  private currentPath: string = '';
  private hostOrigin: string = '';
  private themeListeners = new Set<(theme: FiasTheme) => void>();
  private navigationListeners = new Set<(path: string) => void>();
  private stepNavigationListeners = new Set<(nodeId: string) => void>();
  // Preview-state preservation (preview mode only). Keyed so multiple
  // useFiasPreviewState() callers don't collide. Providers produce a snapshot
  // on `preview_state_capture`; restore listeners receive it after the reload.
  private previewStateProviders = new Map<string, () => unknown>();
  private previewStateRestoreListeners = new Map<string, (state: unknown) => void>();
  // A restore payload that arrived before its hook registered (the host
  // restores on `ready`, but the React effect that registers runs later).
  // Consumed per-key as listeners register.
  private bufferedRestoreState: Record<string, unknown> | null = null;
  // Realtime subscriptions (ADR-012 Phase M4), keyed by the HOST-MINTED
  // logical subscriptionId returned from a subscribe-kind entity_invoke.
  // The plugin never sees grants/topics — the host manages the transport;
  // these listeners receive typed invalidations and terminal endings.
  private subscriptionEventListeners = new Map<
    string,
    Set<(event: { collection: string; changed: true }) => void>
  >();
  private subscriptionEndedListeners = new Map<
    string,
    Set<(reason: SubscriptionEndedReason) => void>
  >();
  private listening = false;

  constructor() {
    this.initPromise = new Promise((resolve) => {
      this.initResolve = resolve;
    });

    this.attach();
  }

  /**
   * Register the message listener. Safe to call multiple times —
   * will not add duplicate listeners.
   *
   * Sends a `request_init` message to the host after attaching. This
   * handles the srcDoc race condition where the host sends `init` before
   * our module has loaded and our listener is attached.
   */
  attach(): void {
    if (typeof window !== 'undefined' && !this.listening) {
      window.addEventListener('message', this.handleMessage);
      this.listening = true;

      // Ask the host to (re-)send init in case we missed the first one
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'request_init' }, '*');
      }
    }
  }

  /**
   * Wait for the host to send the init message.
   */
  async waitForInit(): Promise<void> {
    if (this.initialized) return;
    return this.initPromise!;
  }

  /**
   * Signal to the host that the plugin is loaded and ready. Announces
   * the SDK's bridge protocol version so the host can detect (and surface)
   * an out-of-date plugin before it tries to call ops the host removed.
   */
  ready(): void {
    this.send('ready', {
      sdkProtocolVersion: SDK_BRIDGE_PROTOCOL_VERSION,
      // Advertise preview-state support so the host can snapshot/restore us
      // across a preview rebuild. Older SDKs omit this and the host skips it.
      supportsPreviewState: true,
    });
  }

  /**
   * Request the host to resize the plugin iframe.
   */
  resize(height: number): void {
    this.send('resize', { height });
  }

  /**
   * Show a platform toast notification.
   */
  showToast(message: string, variant: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    this.send('toast', { message, variant });
  }

  /**
   * Send a request to the host and wait for a response.
   */
  async request<T>(
    type: PluginToHostMessageType,
    payload: unknown,
    timeoutMs?: number,
  ): Promise<T> {
    await this.waitForInit();

    return new Promise<T>((resolve, reject) => {
      const messageId = generateMessageId();
      const timer = setTimeout(() => {
        this.pendingRequests.delete(messageId);
        reject(new FiasBridgeError(`Bridge request timed out: ${type}`, 'TIMEOUT'));
      }, timeoutMs ?? DEFAULT_TIMEOUT_MS);

      this.pendingRequests.set(messageId, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timer,
      });

      this.send(type, payload, messageId);
    });
  }

  getArchId(): string {
    return this.archId;
  }

  getPermissions(): PluginPermission[] {
    return [...this.permissions];
  }

  getTheme(): FiasTheme | null {
    return this.theme;
  }

  /**
   * Asset URLs for a platform-vendored library (init payload), or null when the
   * host's CDN is unconfigured, the plugin lacks `sandbox:vendored-libraries`,
   * or the library isn't vendored. Static per session — no update listener
   * needed. `importSpecifier` is the library's bare module name (e.g.
   * `'pdfjs-dist'`). See `useFiasVendoredAssets`.
   */
  getVendoredAssets(importSpecifier: string): Record<string, string> | null {
    return this.vendoredAssets?.[importSpecifier] ?? null;
  }

  getCurrentPath(): string {
    return this.currentPath;
  }

  onThemeUpdate(listener: (theme: FiasTheme) => void): () => void {
    this.themeListeners.add(listener);
    return () => this.themeListeners.delete(listener);
  }

  onNavigationUpdate(listener: (path: string) => void): () => void {
    this.navigationListeners.add(listener);
    return () => this.navigationListeners.delete(listener);
  }

  onStepNavigationUpdate(listener: (nodeId: string) => void): () => void {
    this.stepNavigationListeners.add(listener);
    return () => this.stepNavigationListeners.delete(listener);
  }

  /**
   * Opt into preview-state preservation. In the builder preview, an in-place
   * rebuild reloads the iframe and destroys the plugin's in-memory state (e.g.
   * a generated report). Registering here lets the host snapshot `getState()`
   * before the reload and hand it back to `onRestore` afterwards.
   *
   * `key` namespaces the snapshot so independent callers don't collide. Outside
   * preview mode the host never requests a capture, so this is inert. Returns an
   * unregister function.
   */
  registerPreviewState(
    key: string,
    getState: () => unknown,
    onRestore: (state: unknown) => void,
  ): () => void {
    this.previewStateProviders.set(key, getState);
    this.previewStateRestoreListeners.set(key, onRestore);
    // A restore may have arrived before this registration — deliver it now.
    this.deliverBufferedRestore();
    return () => {
      this.previewStateProviders.delete(key);
      this.previewStateRestoreListeners.delete(key);
    };
  }

  /** Deliver any buffered restore payload to registered listeners (once per key). */
  private deliverBufferedRestore(): void {
    if (!this.bufferedRestoreState) return;
    for (const [key, listener] of this.previewStateRestoreListeners) {
      if (key in this.bufferedRestoreState) {
        try {
          listener(this.bufferedRestoreState[key]);
        } catch {
          // A faulty restore handler must not break the bridge.
        }
        delete this.bufferedRestoreState[key];
      }
    }
    if (Object.keys(this.bufferedRestoreState).length === 0) {
      this.bufferedRestoreState = null;
    }
  }

  /**
   * Send a streaming entity_invoke request. Tokens arrive via stream_token
   * messages and are forwarded to the onToken callback. The returned promise
   * resolves with the final EntityInvocationResult when the host sends the
   * normal response message.
   */
  async requestStream(
    payload: unknown,
    onToken: (text: string) => void,
  ): Promise<EntityInvocationResult> {
    await this.waitForInit();

    return new Promise<EntityInvocationResult>((resolve, reject) => {
      const messageId = generateMessageId();
      const timer = setTimeout(() => {
        this.pendingRequests.delete(messageId);
        reject(new FiasBridgeError('Bridge streaming request timed out: entity_invoke', 'TIMEOUT'));
      }, 120_000); // Longer timeout for streaming

      this.pendingRequests.set(messageId, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timer,
        onToken,
      });

      this.send('entity_invoke', { ...(payload as object), stream: true }, messageId);
    });
  }

  /**
   * Clean up event listeners and pending requests.
   * After destroy(), the bridge can be re-activated by calling attach().
   */
  destroy(): void {
    if (typeof window !== 'undefined' && this.listening) {
      window.removeEventListener('message', this.handleMessage);
      this.listening = false;
    }
    for (const pending of this.pendingRequests.values()) {
      clearTimeout(pending.timer);
      pending.reject(new FiasBridgeError('Bridge destroyed', 'BRIDGE_DESTROYED'));
    }
    this.pendingRequests.clear();
    this.themeListeners.clear();
    this.navigationListeners.clear();
    this.stepNavigationListeners.clear();
    this.previewStateProviders.clear();
    this.previewStateRestoreListeners.clear();
    this.bufferedRestoreState = null;
    this.subscriptionEventListeners.clear();
    this.subscriptionEndedListeners.clear();
  }

  /**
   * Listens for invalidation events on a logical realtime subscription
   * (the id returned by a subscribe-kind entity_invoke). Returns an
   * unsubscribe function for the LISTENER only — releasing the
   * subscription itself is `releaseSubscription`.
   */
  onSubscriptionEvent(
    subscriptionId: string,
    listener: (event: { collection: string; changed: true }) => void,
  ): () => void {
    let set = this.subscriptionEventListeners.get(subscriptionId);
    if (!set) {
      set = new Set();
      this.subscriptionEventListeners.set(subscriptionId, set);
    }
    set.add(listener);
    return () => {
      const listeners = this.subscriptionEventListeners.get(subscriptionId);
      listeners?.delete(listener);
      if (listeners && listeners.size === 0) {
        this.subscriptionEventListeners.delete(subscriptionId);
      }
    };
  }

  /**
   * Listens for the terminal ending of a logical realtime subscription.
   * Reasons are a closed union — `authorization_revoked` and
   * `collection_deleted` are definitive; the rest may be worth a fresh
   * subscribe once conditions change.
   */
  onSubscriptionEnded(
    subscriptionId: string,
    listener: (reason: SubscriptionEndedReason) => void,
  ): () => void {
    let set = this.subscriptionEndedListeners.get(subscriptionId);
    if (!set) {
      set = new Set();
      this.subscriptionEndedListeners.set(subscriptionId, set);
    }
    set.add(listener);
    return () => {
      const listeners = this.subscriptionEndedListeners.get(subscriptionId);
      listeners?.delete(listener);
      if (listeners && listeners.size === 0) {
        this.subscriptionEndedListeners.delete(subscriptionId);
      }
    };
  }

  /**
   * Releases one logical realtime subscription (fire-and-forget). The host
   * refcounts: the underlying socket topic is torn down when the last
   * logical subscriber releases.
   */
  releaseSubscription(subscriptionId: string): void {
    this.subscriptionEventListeners.delete(subscriptionId);
    this.subscriptionEndedListeners.delete(subscriptionId);
    this.send('subscription_unsubscribe', { subscriptionId });
  }

  private handleMessage = (event: MessageEvent): void => {
    const data = event.data;
    if (!data || typeof data !== 'object' || !data.type) return;

    // Handle init message from host (only process once to prevent replay).
    // The first init message establishes the trusted host origin.
    if (data.type === 'init') {
      if (this.initialized) return;
      const initMsg = data as BridgeInitMessage;

      // Protocol version check: refuse to initialize against a host that
      // speaks an older bridge protocol than this SDK requires. Absence of
      // `hostProtocolVersion` is treated as version 1 (the first published
      // contract — older hosts that don't include it are still v1-compatible).
      // See `protocol.ts` for the versioning policy.
      const hostVersion = initMsg.payload.hostProtocolVersion ?? 1;
      if (hostVersion < SDK_REQUIRES_HOST_PROTOCOL_VERSION) {
        // Don't pin hostOrigin or mark initialized — leave the bridge in
        // its pre-init state so the plugin's `waitForInit()` never
        // resolves. The plugin author sees a clear console error rather
        // than running against a host that's missing capabilities the
        // SDK relies on.
        console.error(
          `[fias-sdk] Host bridge protocol v${hostVersion} is below the minimum this SDK supports (v${SDK_REQUIRES_HOST_PROTOCOL_VERSION}). Plugin refused to initialize.`,
        );
        return;
      }

      this.hostOrigin = event.origin;
      this.archId = initMsg.payload.archId;
      this.permissions = initMsg.payload.permissions;
      this.theme = mergeThemeWithDefaults(initMsg.payload.theme);
      this.vendoredAssets = initMsg.payload.vendoredAssets ?? null;
      this.currentPath = initMsg.payload.currentPath;
      this.initialized = true;
      this.initResolve?.();
      // Notify listeners so hooks that subscribed before init arrived
      // (the common case — React's useEffect fires on commit, and init
      // often hasn't landed yet) pick up the initial path. Without this,
      // useFiasNavigation stays at its pre-init `''` until the first
      // navigate_update, which for direct-URL entries (e.g. shared links)
      // never comes — the plugin renders its home page instead of the
      // sub-path.
      for (const listener of this.navigationListeners) {
        listener(this.currentPath);
      }
      return;
    }

    // After init, only accept messages from the verified host origin
    if (this.hostOrigin && event.origin !== this.hostOrigin) return;

    // Handle theme updates
    if (data.type === 'theme_update') {
      this.theme = mergeThemeWithDefaults(data.payload as Partial<FiasTheme>);
      for (const listener of this.themeListeners) {
        listener(this.theme);
      }
      return;
    }

    // Handle navigation updates
    if (data.type === 'navigate_update') {
      this.currentPath = data.payload as string;
      for (const listener of this.navigationListeners) {
        listener(this.currentPath);
      }
      return;
    }

    // Handle step navigation from host
    if (data.type === 'step_navigate') {
      const { nodeId } = data.payload as { nodeId: string };
      if (typeof nodeId === 'string') {
        for (const listener of this.stepNavigationListeners) {
          listener(nodeId);
        }
      }
      return;
    }

    // Host requests a snapshot of the plugin's transient state before a preview
    // rebuild. Reply with a keyed snapshot (empty if nothing registered) so the
    // host's capture resolves promptly either way.
    if (data.type === 'preview_state_capture') {
      const snapshot: Record<string, unknown> = {};
      for (const [key, provider] of this.previewStateProviders) {
        try {
          // Round-trip each slice so a non-serializable value (function,
          // circular ref, DOM node) is dropped here rather than throwing out of
          // postMessage's structured clone and breaking the whole capture.
          snapshot[key] = JSON.parse(JSON.stringify(provider()));
        } catch {
          // Skip a provider that throws or returns non-JSON-safe state.
        }
      }
      this.send('preview_state', snapshot);
      return;
    }

    // Host replays a previously-captured snapshot after the reload. Buffer it so
    // hooks that register slightly later (their effect runs after init) still
    // receive their slice.
    if (data.type === 'preview_state_restore') {
      const state = (data.payload as Record<string, unknown>) ?? {};
      this.bufferedRestoreState = { ...(this.bufferedRestoreState ?? {}), ...state };
      this.deliverBufferedRestore();
      return;
    }

    // Handle streaming tokens
    if (data.type === 'stream_token') {
      const { messageId, text } = data as { type: string; messageId: string; text: string };
      const pending = this.pendingRequests.get(messageId);
      if (pending?.onToken && typeof text === 'string') {
        pending.onToken(text);
      }
      return;
    }

    // Realtime subscription invalidation (ADR-012 Phase M4): notify-then-
    // refetch — the event only says the collection changed.
    if (data.type === 'subscription_event') {
      const payload = data.payload as {
        subscriptionId?: string;
        event?: { collection: string; changed: true };
      };
      if (typeof payload?.subscriptionId === 'string' && payload.event) {
        const listeners = this.subscriptionEventListeners.get(payload.subscriptionId);
        if (listeners) {
          for (const listener of listeners) listener(payload.event);
        }
      }
      return;
    }

    // Terminal ending of a realtime subscription (host-side lease/authz).
    if (data.type === 'subscription_ended') {
      const payload = data.payload as {
        subscriptionId?: string;
        reason?: SubscriptionEndedReason;
      };
      if (typeof payload?.subscriptionId === 'string' && payload.reason) {
        const listeners = this.subscriptionEndedListeners.get(payload.subscriptionId);
        if (listeners) {
          for (const listener of listeners) listener(payload.reason);
        }
        this.subscriptionEventListeners.delete(payload.subscriptionId);
        this.subscriptionEndedListeners.delete(payload.subscriptionId);
      }
      return;
    }

    // Handle response messages
    if (data.type === 'response') {
      const response = data as BridgeResponse;
      const pending = this.pendingRequests.get(response.messageId);
      if (!pending) return;

      clearTimeout(pending.timer);
      this.pendingRequests.delete(response.messageId);

      if (response.error) {
        // The wire format is plain fields (string + optional code); the Error
        // instance is constructed HERE, never structured-cloned across the
        // postMessage boundary.
        pending.reject(
          new FiasBridgeError(
            response.error || 'Bridge request failed',
            response.errorCode ?? 'BRIDGE_ERROR',
          ),
        );
      } else {
        pending.resolve(response.payload);
      }
    }
  };

  private send(type: PluginToHostMessageType, payload: unknown, messageId?: string): void {
    if (typeof window === 'undefined') return;

    const message: BridgeMessage = {
      type,
      messageId: messageId ?? generateMessageId(),
      payload,
    };

    // Use the verified host origin when available; fall back to '*' only
    // for the initial 'ready' message sent before init is received.
    const targetOrigin = this.hostOrigin || '*';
    window.parent.postMessage(message, targetOrigin);
  }
}

/**
 * Singleton bridge instance. Eagerly created at module scope so the
 * message listener is registered as early as possible. When loading via
 * srcDoc, the host may send init before this module executes — the bridge
 * sends a `request_init` message to the host on attach to handle this.
 */
let bridgeInstance: FiasBridge | null = typeof window !== 'undefined' ? new FiasBridge() : null;

export function getBridge(): FiasBridge {
  if (!bridgeInstance) {
    bridgeInstance = new FiasBridge();
  }
  return bridgeInstance;
}

/**
 * Reset the bridge singleton (used in testing).
 */
export function resetBridge(): void {
  bridgeInstance?.destroy();
  bridgeInstance = null;
}
